import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, CheckCircle, Clock, Gift, ChevronRight } from 'lucide-react';
import { gameApi } from '../../api';
import { useAppStore } from '../../store/useAppStore';
import { useTelegram } from '../../hooks';
import { showCombinedRewardedAds } from '../../hooks/useCombinedAds';
import toast from 'react-hot-toast';

interface Quest {
  id: string;
  type: string;
  title: string;
  description: string;
  target: number;
  current: number;
  reward: number;
  completed: boolean;
  claimed: boolean;
}

// Map quest types to their navigation paths
const QUEST_NAVIGATION_MAP: Record<string, string> = {
  'ad_views': '/earn',
  'watch_ads': '/earn',
  'wheel_spins': '/games',
  'wheel': '/games',
  'faucet_claims': '/',
  'claim_faucet': '/',
  'referrals': '/referral',
  'referral': '/referral',
  'auto_cycles': '/earn',
  'auto_faucet': '/earn',
};

export function DailyQuests() {
  const navigate = useNavigate();
  const [quests, setQuests] = useState<Quest[]>([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState<string | null>(null);

  const updateBalance = useAppStore((state) => state.updateBalance);
  const user = useAppStore((state) => state.user);
  const { hapticFeedback, hapticNotification } = useTelegram();

  useEffect(() => {
    fetchQuests();
  }, []);

  const fetchQuests = async () => {
    try {
      const data = await gameApi.getQuests();
      setQuests(data.quests.map(q => ({
        ...q,
        type: q.type || 'default',
        current: q.current || q.progress || 0,
        claimed: q.claimed || false,
      })));
    } catch (error) {
      console.error('Failed to fetch quests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimQuest = async (questId: string) => {
    setClaiming(questId);
    hapticFeedback('medium');

    try {
      // Show combined rewarded ads before claiming (AdsGram first, then Onclicka)
      toast.loading('Showing ads...', { id: 'claim-ads' });
      
      const adResult = await showCombinedRewardedAds('quest_claim');
      
      toast.dismiss('claim-ads');
      
      // Proceed with claim even if ads partially failed (graceful fallback)
      if (!adResult.success) {
        console.log('Ads failed but proceeding with claim:', adResult);
      }
      
      const result = await gameApi.claimQuest(questId);
      
      // Update quest state
      setQuests((prev) =>
        prev.map((q) =>
          q.id === questId ? { ...q, claimed: true } : q
        )
      );

      if (user) {
        updateBalance(result.newBalance);
      }

      hapticNotification('success');
      toast.success(`+${result.reward} points claimed!`);
    } catch (error: any) {
      hapticNotification('error');
      toast.error(error.response?.data?.error || 'Failed to claim');
    } finally {
      setClaiming(null);
    }
  };

  const getQuestIcon = (type: string) => {
    switch (type) {
      case 'claim_faucet':
        return '💧';
      case 'watch_ads':
        return '📺';
      case 'referral':
        return '👥';
      case 'auto_faucet':
        return '⚡';
      case 'wheel':
        return '🎡';
      default:
        return '🎯';
    }
  };

  const completedCount = quests.filter((q) => q.completed).length;
  const claimedCount = quests.filter((q) => q.claimed).length;

  if (loading) {
    return (
      <div className="card p-6">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="card p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Target className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Daily Quests</h3>
            <p className="text-white/60 text-sm">
              {completedCount} / {quests.length} completed
            </p>
          </div>
        </div>

        {/* Timer to reset */}
        <div className="flex items-center gap-1 text-white/50 text-sm">
          <Clock className="w-4 h-4" />
          <span>Resets in 12h</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${(completedCount / quests.length) * 100}%` }}
            className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
          />
        </div>
      </div>

      {/* Quests list */}
      <div className="space-y-3">
        {quests.map((quest, index) => {
          const questPath = QUEST_NAVIGATION_MAP[quest.type] || '/';
          const isClickable = !quest.claimed && !quest.completed;
          
          const handleQuestClick = () => {
            if (isClickable) {
              hapticFeedback('light');
              navigate(questPath);
            }
          };
          
          return (
          <motion.div
            key={quest.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={handleQuestClick}
            className={`relative overflow-hidden rounded-xl transition-all duration-300 ${
              quest.claimed
                ? 'bg-green-500/10 border border-green-500/30'
                : quest.completed
                  ? 'bg-white/10 border border-white/20'
                  : 'bg-white/5 hover:bg-white/10 cursor-pointer'
            }`}
          >
            <div className="p-4 flex items-center gap-4">
              {/* Icon */}
              <div className="text-2xl flex-shrink-0">
                {getQuestIcon(quest.type)}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h4 className="font-medium mb-1 truncate">{quest.title}</h4>
                <p className="text-white/50 text-sm truncate">{quest.description}</p>
                
                {/* Progress bar */}
                {!quest.completed && (
                  <div className="mt-2">
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min((quest.current / quest.target) * 100, 100)}%` }}
                        className="h-full bg-gradient-to-r from-blue-500 to-cyan-500"
                      />
                    </div>
                    <p className="text-xs text-white/40 mt-1">
                      {quest.current} / {quest.target}
                    </p>
                  </div>
                )}
              </div>

              {/* Reward / Claim button */}
              <div className="flex-shrink-0">
                {quest.claimed ? (
                  <div className="flex items-center gap-1 text-green-400">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">Claimed</span>
                  </div>
                ) : quest.completed ? (
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleClaimQuest(quest.id)}
                    disabled={claiming === quest.id}
                    className="px-4 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium text-sm flex items-center gap-1"
                  >
                    {claiming === quest.id ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Gift className="w-4 h-4" />
                        +{quest.reward}
                      </>
                    )}
                  </motion.button>
                ) : (
                  <div className="flex items-center gap-1 text-white/40">
                    <span className="text-sm font-medium">+{quest.reward}</span>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                )}
              </div>
            </div>

            {/* Completed overlay effect */}
            {quest.claimed && (
              <div className="absolute inset-0 bg-green-500/5 pointer-events-none" />
            )}
          </motion.div>
        );
        })}
      </div>

      {/* All completed bonus */}
      {completedCount === quests.length && claimedCount < quests.length && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 p-4 rounded-xl bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 text-center"
        >
          <p className="text-yellow-400 font-medium mb-2">
            🎉 All quests completed!
          </p>
          <p className="text-white/60 text-sm">
            Claim all rewards to unlock bonus points
          </p>
        </motion.div>
      )}

      {claimedCount === quests.length && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 p-4 rounded-xl bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 text-center"
        >
          <p className="text-green-400 font-medium">
            ✅ All quests claimed! Come back tomorrow for more.
          </p>
        </motion.div>
      )}
    </motion.div>
  );
}
